import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import type {
  AnalyticsReport,
  DashboardReport,
  LoyaltyLiability,
  PeriodReport
} from '@billiard/shared'
import { useAuth } from '@/auth/AuthContext'
import { useFetch } from '@/hooks/useApi'
import { useConfigQuery } from '@/context/ClubContext'
import {
  AsyncState,
  BarChart,
  Delta,
  EmptyState,
  ErrorState,
  Kpi,
  Loading,
  SectionHead,
  type BarDatum
} from '@/components/ui'
import { count, dateTime, dateOnly, duration, money, share } from '@/lib/format'
import { Cur, Money } from '@/lib/currency'
import { AccountantOverview } from './AccountantOverview'

/** Начало текущего месяца в epoch-ms (локальная TZ). */
function startOfMonthMs(): number {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0).getTime()
}

/** Начало текущих суток в epoch-ms (локальная TZ). */
function startOfDayMs(): number {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0).getTime()
}

export function Dashboard(): React.JSX.Element {
  const { isSuperAdmin, claims } = useAuth()
  if (isSuperAdmin) return <SuperAdminDashboard />
  // «Бухгалтер» видит свой дашборд (выручка + инкассация + счета + бонусы)
  // вместо общего ClubDashboard, рассчитанного на владельца/админа клуба.
  if (claims?.role === 'accountant') return <AccountantOverview />
  return <ClubDashboard />
}

// ───────────────────────── Кросс-клубный (супер-админ) ─────────────────────────

function SuperAdminDashboard(): React.JSX.Element {
  const { data, loading, error } = useFetch<DashboardReport>('/v1/reports/dashboard')

  return (
    <>
      <div className="eyebrow">Обзор</div>
      <h2 className="page-title">Дашборд — все клубы</h2>
      <p className="page-lead">
        Кросс-клубный ролл-ап по всем арендаторам: выручка, разбивка по оплатам, сессии и открытые
        смены.
      </p>

      {loading && <div style={{ marginTop: 24 }}>{<Loading />}</div>}
      {error && <div style={{ marginTop: 24 }}>{<ErrorState message={error} />}</div>}

      {data && (
        <>
          <SectionHead title="Итого по сети" meta={`Сформировано: ${dateTime(data.generatedAt)}`} />
          <div className="kpi-grid">
            <Kpi label="Выручка" value={<Money value={data.grandTotals.revenue} />} gold accent />
            <Kpi label="Наличные" value={<Money value={data.grandTotals.cash} />} />
            <Kpi label="Безналичные" value={<Money value={data.grandTotals.card} />} />
            <Kpi label="Сессий" value={count(data.grandTotals.count)} />
            <Kpi label="Скидки" value={<Money value={data.grandTotals.discount} />} />
            <Kpi label="Бонусы списано" value={<Money value={data.grandTotals.bonusUsed} />} />
          </div>

          <SectionHead title="По клубам" meta={`${data.perClub.length} клуб(ов)`} />
          {data.perClub.length === 0 ? (
            <EmptyState message="Нет данных по клубам за текущий период." />
          ) : (
            <div className="club-grid">
              {data.perClub.map((club) => (
                <article key={club.clubId} className="club-card">
                  <div className="club-card__name">
                    <span>{club.clubName}</span>
                    {club.openShifts > 0 && (
                      <span className="pill pill--open">{count(club.openShifts)} откр.</span>
                    )}
                  </div>
                  <div className="club-card__revenue">
                    <Money value={club.totals.revenue} />
                  </div>
                  <div className="club-card__rows">
                    <div className="club-card__row">
                      <span className="k">Наличные</span>
                      <span className="v">
                        <Money value={club.totals.cash} />
                      </span>
                    </div>
                    <div className="club-card__row">
                      <span className="k">Безналичные</span>
                      <span className="v">
                        <Money value={club.totals.card} />
                      </span>
                    </div>
                    <div className="club-card__row">
                      <span className="k">Сессий</span>
                      <span className="v">{count(club.totals.count)}</span>
                    </div>
                    <div className="club-card__row">
                      <span className="k">Открытых смен</span>
                      <span className="v">{count(club.openShifts)}</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </>
      )}
    </>
  )
}

// ───────────────────────── Клубный сотрудник ─────────────────────────

function ClubDashboard(): React.JSX.Element {
  const { claims } = useAuth()
  const from = startOfMonthMs()
  const to = Date.now()

  // Окно «сегодня» фиксируем на минуту: иначе `to` менялся бы каждый рендер и
  // useFetch перезапрашивал бы аналитику без остановки.
  const today = useMemo(() => {
    const nowMinute = Math.floor(Date.now() / 60_000) * 60_000
    return { from: startOfDayMs(), to: nowMinute }
  }, [])

  const query = useConfigQuery()

  // clubId для клубного сотрудника выводится из токена на бэкенде — query не нужен.
  const period = useFetch<PeriodReport>('/v1/reports/period', { from, to }, [from])
  const liability = useFetch<LoyaltyLiability>('/v1/reports/loyalty/liability')
  const day = useFetch<AnalyticsReport>(
    '/v1/reports/analytics',
    { ...query, from: today.from, to: today.to },
    [query?.clubId, today.from, today.to]
  )

  return (
    <>
      <div className="eyebrow">Обзор</div>
      <h2 className="page-title">Дашборд</h2>
      <p className="page-lead">
        Сегодняшние продажи по часам и столам, сводка за текущий месяц и обязательства по программе
        лояльности
        {claims?.club_id ? '.' : ' (клуб не задан в токене).'}
      </p>

      <SectionHead
        title="Сегодня"
        meta={
          <>
            {dateOnly(today.from)} · <Link to="/analytics">Подробная аналитика →</Link>
          </>
        }
      />
      <AsyncState query={day} loadingLabel="Считаем сегодняшние продажи…" />
      {day.data && <TodayBlock report={day.data} />}

      <SectionHead title="Текущий месяц" meta={`с ${dateOnly(from)} по ${dateOnly(to)}`} />

      <AsyncState query={period} />
      {period.data && (
        <div className="kpi-grid">
          <Kpi label="Выручка" value={<Money value={period.data.totals.revenue} />} gold accent />
          <Kpi label="Наличные" value={<Money value={period.data.totals.cash} />} />
          <Kpi label="Безналичные" value={<Money value={period.data.totals.card} />} />
          <Kpi label="Сессий" value={count(period.data.totals.count)} />
          <Kpi label="Скидки" value={<Money value={period.data.totals.discount} />} />
          <Kpi label="Бонусы списано" value={<Money value={period.data.totals.bonusUsed} />} />
        </div>
      )}

      <SectionHead title="Программа лояльности" />
      <AsyncState query={liability} />
      {liability.data && (
        <div className="kpi-grid">
          <Kpi label="Клиентов" value={count(liability.data.customers)} />
          <Kpi
            label="Долг по бонусам"
            value={<Money value={liability.data.totalBonusBalance} />}
            gold
            accent
            sub="Сумма бонусных балансов клиентов"
          />
        </div>
      )}
    </>
  )
}

/**
 * Сегодняшние продажи: итоги, график по часам и разбивка по столам.
 * Предыдущее окно, с которым сравниваются KPI, бэкенд берёт той же длины —
 * то есть вчерашний день на это же время, а не вчерашние сутки целиком.
 */
function TodayBlock({ report }: { report: AnalyticsReport }): React.JSX.Element {
  const t = report.totals
  const prev = report.previous.totals
  const hours = report.byHour
  const maxHour = hours.reduce((m, h) => Math.max(m, h.amount), 0)
  const bars: BarDatum[] = hours.map((h) => ({
    key: String(h.hour),
    label: String(h.hour).padStart(2, '0'),
    value: h.amount,
    peak: h.amount > 0 && h.amount === maxHour,
    title: `${String(h.hour).padStart(2, '0')}:00 — ${money(h.amount)}, ${count(h.count)} чек(ов)`
  }))
  const tables = report.byTable.filter((r) => r.count > 0)
  const tablesTotal = tables.reduce((s, r) => s + r.amount, 0)

  return (
    <>
      <div className="kpi-grid">
        <Kpi
          label="Выручка за сегодня"
          value={<Money value={t.revenue} />}
          gold
          accent
          sub={
            <>
              <Delta current={t.revenue} prev={prev.revenue} /> ко вчера на это же время
            </>
          }
        />
        <Kpi
          label="Чеков"
          value={count(t.count)}
          sub={<Delta current={t.count} prev={prev.count} />}
        />
        <Kpi label="Средний чек" value={<Money value={t.avgCheck} />} />
        <Kpi label="Наличные" value={<Money value={t.cash} />} />
        <Kpi label="Безналичные" value={<Money value={t.card} />} />
        <Kpi
          label="Часов игры"
          value={duration(t.minutes)}
          sub={report.peaks.bestHour ? `Пик — ${report.peaks.bestHour.hour}:00` : undefined}
        />
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <SectionHead title="Продажи по часам" meta="Чек отнесён к часу открытия стола" />
        {maxHour === 0 ? (
          <EmptyState message="Сегодня продаж ещё не было." />
        ) : (
          <BarChart data={bars} maxTicks={24} height={150} minColWidth={18} />
        )}

        <SectionHead title="По столам" meta={`${count(tables.length)} стол(ов) в игре`} />
        {tables.length === 0 ? (
          <EmptyState message="Столы сегодня не открывались." />
        ) : (
          <div className="table-wrap">
            <table className="rtable">
              <thead>
                <tr>
                  <th>Стол</th>
                  <th className="num">Чеков</th>
                  <th className="num">Выручка</th>
                  <th className="num">Доля</th>
                  <th className="num">Часов игры</th>
                </tr>
              </thead>
              <tbody>
                {tables.map((r) => (
                  <tr key={r.id || r.name}>
                    <td>{r.name}</td>
                    <td className="num">{count(r.count)}</td>
                    <td className="num">
                      {money(r.amount)} <Cur />
                    </td>
                    <td className="num">{share(r.amount, tablesTotal)}</td>
                    <td className="num">{duration(r.minutes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}
